<template>
  <b-form @submit.prevent="submit">
    <b-row>
      <b-col cols="12" class="h5 font-weight-bold mb-4">
        {{ learningUnit && learningUnit.id ? 'Edit' : 'Create' }}
        Micro-Credentials
      </b-col>

      <!-- Title -->
      <b-col cols="12" class="mb-4">
        <label ref="label__mc-title" for="input__mc-title" class="mb-u4">Title</label>
        <b-form-input
          id="input__mc-title"
          v-model="$v.form.name.$model"
          :state="$v.form.name.$anyError || isNameDuplicate ? false : null"
          placeholder="Insert your title"
          aria-describedby="input__mc-title-invalid-feedback"
          class="input__title"
          :disabled="isFieldDisabled('title')"
          @blur="checkDuplicateMc"
          @click="isNameDuplicate = false"
        >
        </b-form-input>
        <b-form-invalid-feedback
          v-if="!$v.form.name.required"
          id="input__mc-title-invalid-feedback"
        >
          The Title is a required field.
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.form.name.minLength"
          id="input__mc-title-invalid-feedback"
        >
          The Title must be at least 3 characters.
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.form.name.maxLength"
          id="input__mc-title-invalid-feedback"
        >
          The Title not be greater than 255 characters.
        </b-form-invalid-feedback>
        <b-form-invalid-feedback v-if="isNameDuplicate" id="input__mc-title-invalid-feedback">
          This title is already existed.
        </b-form-invalid-feedback>
      </b-col>

      <!-- Description -->
      <b-col cols="12" class="mb-4">
        <label ref="label__mc-description" for="input__mc-description" class="mb-u4">
          Description
        </label>
        <base-text-editor
          id="input__mc-description"
          v-model="$v.form.description.$model"
          name="input__mc-description"
          :class="{
            text_editor__required: $v.form.description.$anyError
          }"
          :disabled="isFieldDisabled('description')"
          :show-toolbar="!isFieldDisabled('description')"
          placeholder="Insert your description"
        ></base-text-editor>
        <div
          v-if="$v.form.description.$anyError"
          id="input__mc-description-invalid-feedback"
          class="text_feedback"
        >
          The Description is a required field.
        </div>
      </b-col>

      <!-- Media Detail -->
      <b-col cols="12">
        <base-upload-media
          id="input__media_detail"
          ref="mediaDetailUploader"
          key="mediaDetailUploader"
          class="mb-4"
          :required="onSubmit"
          :reference-meta="referenceMeta"
          field-upload="media_detail"
          :file.sync="formMedia.media_detail"
          :has-file.sync="file.media_detail"
          :disabled="isFieldDisabled('media_detail')"
          @upload:video="
            (v) => {
              video.media_detail = v;
            }
          "
        >
          <template v-if="isUploadMediaDetail" slot="progress-bar">
            <base-progress-bar-video-upload class="mt-3" :percentage="percentageMediaDetail" />
          </template>
        </base-upload-media>
      </b-col>
      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Type -->
      <b-col>
        <LearningUnitTypeSelector
          v-model="formType"
          :disabled="isFieldDisabled('type')"
        ></LearningUnitTypeSelector>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>
      <b-col cols="12" class="h5 font-weight-bold mb-4">{{ badgeText.text }}</b-col>
      <!-- Badge image -->
      <b-col cols="12">
        <base-upload-media
          id="input__badge_image"
          ref="badgeImageUploader"
          key="badgeImageUploader"
          :header="badgeText.header"
          class="mb-4"
          :required="onSubmit"
          :reference-meta="referenceMeta"
          field-upload="badge_image"
          :file.sync="formMedia.badge_image"
          :has-file.sync="file.badge_image"
          :disabled="isFieldDisabled('badge_image')"
          accepted-files=".jpeg, .jpg, .png, .gif"
        ></base-upload-media>
      </b-col>
      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <b-col cols="12" class="h5 font-weight-bold mb-4">Competency overview</b-col>

      <!-- Competency Overview -->
      <b-col cols="12" class="mb-4">
        <label for="input__mc-overview" class="mb-u4"> Overview </label>
        <base-text-editor
          id="input__mc-overview"
          v-model="$v.form.competency_overview.$model"
          name="input__mc-overview"
          :class="{
            text_editor__required: $v.form.competency_overview.$anyError
          }"
          :disabled="isFieldDisabled('competency')"
          :show-toolbar="!isFieldDisabled('competency')"
          placeholder="Insert your overview"
        >
        </base-text-editor>
        <div
          v-if="$v.form.competency_overview.$anyError"
          id="input__mc-overview-invalid-feedback"
          class="text_feedback"
        >
          The Overview is a required field.
        </div>
      </b-col>

      <!-- Competency Detail -->
      <b-col cols="12" class="mb-4">
        <label ref="label__mc-competency" for="input__mc-competency" class="mb-u4">
          Competency
        </label>
        <base-text-editor
          id="input__mc-competency"
          v-model="$v.form.competency_detail.$model"
          name="input__mc-competency"
          :class="{
            text_editor__required: $v.form.competency_detail.$anyError
          }"
          :disabled="isFieldDisabled('competency')"
          :show-toolbar="!isFieldDisabled('competency')"
          placeholder="Insert your competency"
        ></base-text-editor>
        <div
          v-if="$v.form.competency_detail.$anyError"
          id="input__mc-competency-invalid-feedback"
          class="text_feedback"
        >
          The Competency is a required field.
        </div>
      </b-col>

      <!-- Competency How -->
      <b-col cols="12" class="mb-4">
        <label for="input__mc-how" class="mb-u4"> How ? </label>
        <base-text-editor
          id="input__mc-how"
          v-model="$v.form.competency_how.$model"
          name="input__mc-how"
          :class="{
            text_editor__required: $v.form.competency_how.$anyError
          }"
          :disabled="isFieldDisabled('competency')"
          :show-toolbar="!isFieldDisabled('competency')"
          placeholder="Insert your how "
        >
        </base-text-editor>
        <div
          v-if="$v.form.competency_how.$anyError"
          id="input__mc-how-invalid-feedback"
          class="text_feedback"
        >
          The How is a required field.
        </div>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Method components -->
      <b-col cols="12" class="mb-4">
        <LearningUnitMethodFieldForm v-model="formMethod" :disabled="isFieldDisabled('method')" />
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Category  -->
      <b-col>
        <Learning-unit-categories-selector
          ref="dropdown__category-selector"
          v-model="form.category"
          :disabled="isFieldDisabled('category')"
        />
        <div
          v-if="$v.form.category.$anyError"
          id="input__mc-category-invalid-feedback"
          class="text_feedback mt-3"
        >
          Please select micro-credentials category.
        </div>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Price -->
      <b-col cols="12" class="h5 font-weight-bold mb-4">Open to apply</b-col>
      <b-col cols="12">
        <b-row>
          <b-col cols="10" sm="11" class="mb-3">
            <b-form-checkbox
              ref="toggle__verify-plan"
              v-model="form.open_apply_verify"
              switch
              class="toggle__verify-plan"
              :disabled="isFieldDisabled('price_toggle')"
              @change="onChangeVerify"
            >
              <h5>Verify</h5>
              <span> Verify your competence. </span>
            </b-form-checkbox>
          </b-col>

          <b-col cols="10" sm="11" class="mb-3">
            <b-row style="padding-left: 3.4rem">
              <learning-unit-price-input
                ref="priceVerifyInput"
                key="verify-price"
                v-model="formPriceVerify"
                price-title="verify-price"
                :disabled="!form.open_apply_verify || isFieldDisabled('price_input')"
                :v-if="formPriceVerify"
              ></learning-unit-price-input>

              <b-col v-if="form.open_apply_verify" cols="12" class="p-0">
                <b-col cols="12" class="mt-5 mb-3">
                  <b-form-checkbox
                    ref="toggle__learn-verify-plan"
                    v-model="form.open_apply_learn"
                    switch
                    :disabled="isFieldDisabled('price_toggle')"
                    class="toggle__learn-verify-plan"
                    @change="onChangeLearn"
                  >
                    <h5>Learn + Verify</h5>
                    <span> Learn more before verification. </span>
                  </b-form-checkbox>
                </b-col>
                <b-col cols="12">
                  <b-row>
                    <LearningUnitPriceInput
                      v-if="formPriceLearn"
                      ref="priceLearnInput"
                      key="verify-learn-price"
                      v-model="formPriceLearn"
                      price-title="verify-learn-price"
                      :disabled="!form.open_apply_learn || isFieldDisabled('price_input')"
                    >
                    </LearningUnitPriceInput>
                  </b-row>
                </b-col>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="10" sm="11">
            <b-form-checkbox
              ref="toggle__payment-plan"
              v-model="form.is_payment"
              switch
              class="toggle__payment-plan"
            >
              <h5>Enable payment</h5>
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Who is this for -->
      <b-col cols="12" class="h5 font-weight-bold mb-4">Who is this for ?</b-col>
      <b-col cols="12" class="mb-4">
        <label ref="label__who-this-for" for="input__who-this-for" class="mb-u4">
          Description
        </label>
        <base-text-editor
          id="input__who-this-for"
          v-model="$v.form.who_this_for.$model"
          name="input__who-this-for"
          :class="{
            text_editor__required: $v.form.who_this_for.$anyError
          }"
          :disabled="isFieldDisabled('who_this_for')"
          :show-toolbar="!isFieldDisabled('who_this_for')"
          placeholder="e.g, Anyone who curious about we programming"
        ></base-text-editor>
        <div
          v-if="$v.form.who_this_for.$anyError"
          id="input__who-this-for-invalid-feedback"
          class="text_feedback"
        >
          The Description is a required field.
        </div>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Develpor -->
      <b-col cols="12" class="h5 font-weight-bold mb-4">Who developed this ?</b-col>
      <b-col cols="12" class="mb-4">
        <label ref="label__developer-name" for="input__developer-name" class="mb-u4">Name</label>
        <b-form-input
          id="input__developer-name"
          v-model="$v.form.developer_name.$model"
          :state="$v.form.developer_name.$anyError ? false : null"
          placeholder="Insert your name"
          aria-describedby="input__developer-name-invalid-feedback"
          class="input__developer-name"
          :disabled="isFieldDisabled('developer')"
        >
        </b-form-input>
        <b-form-invalid-feedback
          v-if="!$v.form.developer_name.required"
          id="input__developer-name-invalid-feedback"
        >
          The Name is a required field.
        </b-form-invalid-feedback>
      </b-col>

      <b-col cols="12" class="mb-4">
        <label ref="label__developer-detail" for="input__developer-detail" class="mb-u4">
          Description
        </label>
        <base-text-editor
          id="input__developer-detail"
          v-model="$v.form.developer_detail.$model"
          name="input__developer-detail"
          :class="{
            text_editor__required: $v.form.developer_detail.$anyError
          }"
          :disabled="isFieldDisabled('developer')"
          :show-toolbar="!isFieldDisabled('developer')"
          placeholder="Insert your detail"
        ></base-text-editor>
        <div
          v-if="$v.form.developer_detail.$anyError"
          id="input__developer-detail-invalid-feedback"
          class="text_feedback"
        >
          The Detail is a required field.
        </div>
      </b-col>

      <b-col cols="12">
        <base-upload-media
          id="input__developer_media"
          ref="developerImageUploader"
          key="developerImageUploader"
          class="mb-4"
          :required="onSubmit"
          :reference-meta="referenceMeta"
          field-upload="developer_media"
          :file.sync="formMedia.developer_media"
          :has-file.sync="file.developer_media"
          :disabled="isFieldDisabled('developer')"
          accepted-files=".jpeg, .jpg, .png, .gif"
        >
          <template v-slot:description>
            <div class="text-black-50">
              This image will be a thumbnail of this Micro-Credential.
            </div>
          </template>
        </base-upload-media>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Original Language -->
      <b-col cols="12">
        <LearningUnitLanguageSelector
          v-model="formOriginalLanguage"
          :disabled="isFieldDisabled('original_lang')"
        >
        </LearningUnitLanguageSelector>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <b-col cols="12" class="mb-4">
        <b-button variant="success" type="submit" class="btn__create-mc mr-2" :disabled="disabled">
          <div v-if="disabled" class="mb-u1">
            <b-spinner label="Loading..." variant="secondary" small />
          </div>
          <div v-else>
            {{ learningUnit && learningUnit.id ? 'Save' : 'Create' }}
          </div>
        </b-button>
        <b-button
          variant="link"
          class="btn__back"
          :disabled="disabled"
          @click="$_pageNavigationMixin_gotoPreviousPage"
          >Back to previous page
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { omit } from 'lodash';
import { required, minLength, maxLength} from 'vuelidate/lib/validators';
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import BaseUploadMedia from '@/components/base-components/BaseUploadMedia';
import BaseProgressBarVideoUpload from '@/components/base-components/BaseProgressBarVideoUpload';
import LearningUnitPriceInput from '@/components/LearningUnit/LearningUnitPriceInput';
import LearningUnitTypeSelector from '@/components/LearningUnit/LearningUnitTypeSelector';
import LearningUnitCategoriesSelector from './LearningUnitCategoriesSelector.vue';
import LearningUnitMethodFieldForm from './LearningUnitMethodFieldForm.vue';
import LearningUnitLanguageSelector from './LearningUnitLanguageSelector.vue';
import LearningUnit from '@/models/LearningUnit';
import Vimeo from '@/models/Vimeo';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { notificationMixin } from '@/mixins/notificationMixin';
import * as tus from 'tus-js-client';
import { useFileSizeConverter } from '@/composables/useFileSizeConverter';
import { LEARNING_UNIT_LEVEL, ORIGINAL_LANGUAGE } from '@/constants';
import { pageNavigationMixin } from '@/mixins/pageNavigationMixin';
import Category from '@/models/Category';
const { formatMegaBytes } = useFileSizeConverter();

export default {
  components: {
    BaseTextEditor,
    BaseUploadMedia,
    BaseProgressBarVideoUpload,
    LearningUnitPriceInput,
    LearningUnitTypeSelector,
    LearningUnitCategoriesSelector,
    LearningUnitMethodFieldForm,
    LearningUnitLanguageSelector
  },
  mixins: [userInfoMixin, notificationMixin, pageNavigationMixin],

  props: {
    learningUnit: {
      type: LearningUnit,
      default: null
    },

    editable: {
      type: Object,
      default: () => ({
        title: true,
        description: true,
        media_detail: true,
        badge_image: true,
        type: true,
        category: true,
        competency: true,
        price_toggle: true,
        price_input: true,
        who_this_for: true,
        developer: true,
        method: true,
        original_lang: true
      })
    }
  },

  data() {
    return {
      disabled: false,
      onSubmit: false,
      form: {
        name: '',
        description: '',
        media_detail_vimeo: null,
        competency_overview: '',
        competency_detail: '',
        competency_how: '',
        category: null,
        price_verify: null,
        price_verify_discount: null,
        price_verify_learn: null,
        price_verify_learn_discount: null,
        who_this_for: '',
        developer_name: '',
        developer_detail: '',
        open_apply_verify: false,
        open_apply_learn: false,
        level_type: LEARNING_UNIT_LEVEL.CAREER_SPECIFIC,
        method_overview: null,
        how_achieve: null,
        is_payment: true,
        original_lang: ORIGINAL_LANGUAGE.ENGLISH
      },
      formMedia: {
        media_detail: [],
        badge_image: [],
        developer_media: []
      },
      file: {
        media_detail: false,
        badge_image: false,
        developer_media: false
      },
      isNameDuplicate: false,
      referenceMeta: {
        ref: 'learning-unit',
        refId: null
      },
      video: {
        media_detail: null,
        upload_link_vimeo_detail: null,
        video_link_vimeo_detail: null
      },
      isUploadMediaDetail: false,
      percentageMediaDetail: 0
    };
  },

  validations() {
    return {
      form: {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(255)
        },
        description: { required },
        competency_overview: {},
        category: { required },
        competency_detail: { required },
        competency_how: {},
        who_this_for: { required },
        developer_name: { required },
        developer_detail: { required }
      }
    };
  },

  computed: {
    formPriceVerify: {
      get() {
        return {
          normalPrice: this.form.price_verify,
          discountPrice: this.form.price_verify_discount
        };
      },

      set(value) {
        this.form = {
          ...this.form,
          price_verify: value.normalPrice,
          price_verify_discount: value.discountPrice
        };
      }
    },
    formPriceLearn: {
      get() {
        return {
          normalPrice: this.form.price_verify_learn,
          discountPrice: this.form.price_verify_learn_discount
        };
      },

      set(value) {
        this.form = {
          ...this.form,
          price_verify_learn: value.normalPrice,
          price_verify_learn_discount: value.discountPrice
        };
      }
    },
    formType: {
      get() {
        return {
          level_type: this.form.level_type
        };
      },

      set(value) {
        this.form = {
          ...this.form,
          level_type: value.level_type
        };
      }
    },
    formMethod: {
      get() {
        return {
          method_overview: this.form.method_overview,
          how_achieve: this.form.how_achieve
        };
      },
      set(value) {
        this.form = {
          ...this.form,
          method_overview: value.method_overview,
          how_achieve: value.how_achieve
        };
      }
    },
    formOriginalLanguage: {
      get() {
        return {
          original_lang: this.form.original_lang
        };
      },

      set(value) {
        this.form = {
          ...this.form,
          original_lang: value.original_lang
        };
      }
    },
    badgeText() {
      return this.form.level_type === LEARNING_UNIT_LEVEL.CAREER_SPECIFIC
        ? { text: 'Digital Badge', header: 'Upload digital badge' }
        : { text: 'Digital Certificate', header: 'Upload digital certificate' };
    }
  },

  created() {
    this.initMicroCredential();
  },

  methods: {
    isFieldDisabled(field) {
      return !this.editable[field] || this.disabled;
    },

    onChangeVerify(v) {
      if (!v) {
        this.form.open_apply_learn = false;
        this.form.price_verify =
          this.learningUnit && this.learningUnit.price_verify
            ? this.learningUnit.price_verify
            : null;
        this.form.price_verify_discount =
          this.learningUnit && this.learningUnit.price_verify_discount
            ? this.learningUnit.price_verify_discount
            : null;
        this.form.price_verify_learn =
          this.learningUnit && this.learningUnit.price_verify_learn
            ? this.learningUnit.price_verify_learn
            : null;
        this.form.price_verify_learn_discount =
          this.learningUnit && this.learningUnit.price_verify_learn_discount
            ? this.learningUnit.price_verify_learn_discount
            : null;
      }
    },

    onChangeLearn(v) {
      if (!v) {
        this.form.price_verify_learn =
          this.learningUnit && this.learningUnit.price_verify_learn
            ? this.learningUnit.price_verify_learn
            : null;
        this.form.price_verify_learn_discount =
          this.learningUnit && this.learningUnit.price_verify_learn_discount
            ? this.learningUnit.price_verify_learn_discount
            : null;
      }
    },

    async initMicroCredential() {
      if (!this.learningUnit) return;

      const { learningUnit, form } = this;
      for (const [key] of Object.entries(omit(form, ['category']))) {
        this.$set(this.form, key, learningUnit[key]);
      }

      if (learningUnit.categories_ids.length) {
        //Currently, we allow to choose 1 category and recommendation is not allowed to be selected
        const categoryId = learningUnit.categories_ids.find((id) => {
          const category = Category.find(id);
          return category.slug !== 'recommendation';
        });

        if (categoryId) {
          this.$set(this.form, 'category', categoryId);
        }
      }


      const {
        id,
        media_detail_vimeo_id,
        media_detail = [],
        badge_image = [],
        developer_media = []
      } = this.learningUnit;

      this.$set(this.referenceMeta, 'refId', id);

      if (media_detail_vimeo_id) {
        const video = Vimeo.find(media_detail_vimeo_id);
        this.$set(this.form, 'media_detail_vimeo', media_detail_vimeo_id);
        this.$set(this.formMedia, 'media_detail', [video]);
        this.video.media_detail = video;
        this.video.video_link_vimeo_detail = video.video_link;
      }

      if (media_detail.length) {
        this.$set(this.formMedia, 'media_detail', media_detail);
      }

      if (badge_image.length) {
        this.$set(this.formMedia, 'badge_image', badge_image);
      }

      if (developer_media.length) {
        this.$set(this.formMedia, 'developer_media', developer_media);
      }
    },

    async checkDuplicateMc() {
      const oldNameMc = this.learningUnit ? this.learningUnit.name : '';
      if (
        this.form.name.trim().length === 0 ||
        (oldNameMc &&
          oldNameMc.trim().toLowerCase().replace(/\s/g, '') ===
            this.form.name.trim().toLowerCase().replace(/\s/g, ''))
      )
        return;

      const {
        response: { data }
      } = await this.$models['learning-units'].api().checkDuplicateName(this.form.name.trim());

      this.isNameDuplicate = data;
    },

    async resumableUpload(video, params = {}, type = '') {
      const body = {
        ...params,
        size: video.size
      };

      try {
        const { response } = await Vimeo.api().getVimeoUploadLink(body);
        let upload_link = '';
        if (response) {
          upload_link = response.data.upload_link;
          if (type == 'detail') {
            this.$set(this.video, `upload_link_vimeo_detail`, response.data.upload_link);
            this.$set(this.video, `video_link_vimeo_detail`, response.data.link);
          }
        } else {
          console.error('response is ', response);
          throw 'ERROR : form_based_viemo is not exist';
        }

        const responseTusUpload = await new Promise((resolve) => {
          let upload = new tus.Upload(video, {
            uploadUrl: upload_link,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            uploadSize: video.size,
            metadata: {
              filename: video.name,
              filesize: video.size,
              filetype: video.type
            },
            onError: function (error) {
              console.log('Failed because: ' + error);
              this.isLoading = false;
              this.notiError();
              console.error('error', error);
            },

            // Callback for reporting upload progress
            onProgress: (bytesUploaded, bytesTotal) => {
              if (type == 'detail') {
                this.percentageMediaDetail = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
              }
            },

            // Callback for once the upload is completed
            onSuccess: async () => {
              const minInterval = 30;
              const baseSize = 5;
              const sizeMB = formatMegaBytes(video.size, 0);
              const numInterval = Math.ceil(sizeMB / baseSize) + 1; // calculate num interval of video from total size and base size, plus 1 interval

              await Vimeo.api().getVideoInformation(response.data.id, {
                numInterval: numInterval < minInterval ? minInterval : numInterval
              });

              resolve(upload);
            }
          });

          upload.findPreviousUploads().then(function (previousUploads) {
            // Found previous uploads so we select the first one.
            if (previousUploads.length) {
              upload.resumeFromPreviousUpload(previousUploads[0]);
            }

            // Start the upload
            upload.start();
          });
        });

        this.isUploadMediaDetail = false;
        return responseTusUpload;
      } catch (error) {
        this.isLoading = false;
        this.notiError();
        console.error('error', error);
      }
    },

    async create() {
      const form = { ...this.form };

      this.$set(form, 'categories', [form.category]);
      const {
        response: { data }
      } = await LearningUnit.api().createLearningUnit({
        ...form,
        user: this.userId
      });

      const { id } = data;

      this.$set(this.referenceMeta, 'refId', id);

      if (this.video.media_detail) {
        this.isUploadMediaDetail = true;
        await this.resumableUpload(
          this.video.media_detail,
          {
            learning_unit_detail_id: id,
            learning_unit_content_id: null
          },
          'detail'
        );
      } else if (this.$refs.mediaDetailUploader.$children[0].hasAnyQueuedFiles()) {
        await this.$refs.mediaDetailUploader.$children[0].upload();
      }

      if (this.$refs.badgeImageUploader.$children[0].hasAnyQueuedFiles()) {
        await this.$refs.badgeImageUploader.$children[0].upload();
      }

      if (this.$refs.developerImageUploader.$children[0].hasAnyQueuedFiles()) {
        await this.$refs.developerImageUploader.$children[0].upload();
      }

      this.notiSuccessDefault(`The Micro-Credentials was successfully created.`);
    },

    async update() {
      const form = { ...this.form };

      // if new media detail was uploaded, but has old file
      if (
        this.$refs.mediaDetailUploader &&
        this.$refs.mediaDetailUploader.$children[0].hasAnyQueuedFiles()
      ) {
        // if there're old media detail, set media_detail to empty array to clean old file relation.
        if (this.formMedia.media_detail.length) {
          this.$set(form, 'media_detail', []);
        }

        // if there's old vimeo, set media_detail_vimeo to null to clean old vimeo relation.
        if (form.media_detail_vimeo !== null) {
          this.$set(form, 'media_detail_vimeo', null);
        }
      }

      // if new badge image was uploaded, but has old file, set badge_image to empty array to clean old file relation.
      if (
        this.formMedia.badge_image.length &&
        this.$refs.badgeImageUploader &&
        this.$refs.badgeImageUploader.$children[0].hasAnyQueuedFiles()
      ) {
        this.$set(form, 'badge_image', []);
      }

      // if new developer image was uploaded, but has old file, set developer_media to empty array to clean old file relation.
      if (
        this.formMedia.developer_media.length &&
        this.$refs.developerImageUploader &&
        this.$refs.developerImageUploader.$children[0].hasAnyQueuedFiles()
      ) {
        this.$set(form, 'developer_media', []);
      }

      if (form.price_verify_discount === '') {
        this.$set(form, 'price_verify_discount', null);
      }

      if (form.price_verify_learn_discount === '') {
        this.$set(form, 'price_verify_learn_discount', null);
      }

      //Check if the learning unit is recommended
      const recommendedCategory = Category.query().where('slug', 'recommendation').first();
      //If the learning unit is recommended, add the recommended category to the form
      this.$set(form, 'categories', [
        form.category,
        this.learningUnit.isRecommended() ? recommendedCategory.id : null
      ]);

      const {
        response: { data }
      } = await LearningUnit.api().updateLearningUnit(this.learningUnit.id, {
        ...form
      });

      const {
        price_verify,
        price_verify_discount,
        price_verify_learn,
        price_verify_learn_discount,
        open_apply_verify,
        open_apply_learn
      } = form;

      await LearningUnit.api().updatePriceDisplay(this.learningUnit.id, {
        price_verify,
        price_verify_discount,
        price_verify_learn,
        price_verify_learn_discount,
        open_apply_verify,
        open_apply_learn
      });

      const { id } = data;

      if (this.$refs.mediaDetailUploader.$children[0].hasAnyDeletedFiles()) {
        await this.$refs.mediaDetailUploader.$children[0].deleteRemovedFiles();
      }

      if (this.$refs.badgeImageUploader.$children[0].hasAnyDeletedFiles()) {
        await this.$refs.badgeImageUploader.$children[0].deleteRemovedFiles();
      }

      if (this.$refs.developerImageUploader.$children[0].hasAnyDeletedFiles()) {
        await this.$refs.developerImageUploader.$children[0].deleteRemovedFiles();
      }

      if (this.video.media_detail && !this.video.media_detail.id) {
        // trigger upload video when only new video was attached.
        this.isUploadMediaDetail = true;
        await this.resumableUpload(
          this.video.media_detail,
          {
            learning_unit_detail_id: id,
            learning_unit_content_id: null
          },
          'detail'
        );
      } else if (this.$refs.mediaDetailUploader.$children[0].hasAnyQueuedFiles()) {
        await this.$refs.mediaDetailUploader.$children[0].upload();
      }

      if (this.$refs.badgeImageUploader.$children[0].hasAnyQueuedFiles()) {
        await this.$refs.badgeImageUploader.$children[0].upload();
      }

      if (this.$refs.developerImageUploader.$children[0].hasAnyQueuedFiles()) {
        await this.$refs.developerImageUploader.$children[0].upload();
      }

      this.notiSuccessDefault(`The Micro-Credentials was successfully updated`);
    },

    async submit() {
      this.onSubmit = true;

      this.$v.$touch();

      await this.checkDuplicateMc();

      let isPriceVerifyInvalid = false;

      let isPriceLearnInvalid = false;

      if (this.form.open_apply_verify) {
        this.$refs.priceVerifyInput.$v.form.$touch();

        isPriceVerifyInvalid = this.$refs.priceVerifyInput.$v.form.$invalid;
      }

      if (this.form.open_apply_learn) {
        this.$refs.priceLearnInput.$v.form.$touch();

        isPriceLearnInvalid = this.$refs.priceLearnInput.$v.form.$invalid;
      }

      const { media_detail, badge_image, developer_media } = this.file;

      if (this.isNameDuplicate || this.$v.form.name.$invalid) {
        return this.$refs['label__mc-title'].scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (this.$v.form.description.$invalid) {
        return this.$refs['label__mc-description'].scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (!media_detail) {
        return this.$refs.mediaDetailUploader.$el.scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (!badge_image) {
        return this.$refs.badgeImageUploader.$el.scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (this.$v.form.competency_detail.$invalid) {
        return this.$refs['label__mc-competency'].scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (this.$v.form.category.$invalid) {
        return this.$refs['dropdown__category-selector'].$el.scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (isPriceVerifyInvalid && this.$refs['toggle__verify-plan']) {
        return this.$refs['toggle__verify-plan'].$el.scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (isPriceLearnInvalid && this.$refs['toggle__learn-verify-plan']) {
        return this.$refs['toggle__learn-verify-plan'].$el.scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (this.$v.form.who_this_for.$invalid) {
        return this.$refs['label__who-this-for'].scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (this.$v.form.developer_name.$invalid) {
        return this.$refs['label__developer-name'].scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (this.$v.form.developer_detail.$invalid) {
        return this.$refs['label__developer-detail'].scrollIntoView({
          behavior: 'smooth'
        });
      }

      if (!developer_media) {
        return this.$refs.developerImageUploader.$el.scrollIntoView({
          behavior: 'smooth'
        });
      }

      this.disabled = true;

      try {
        if (this.learningUnit && this.learningUnit.id) {
          await this.update();
        } else {
          await this.create();
        }

        this.onSubmit = false;

        this.$_pageNavigationMixin_gotoPreviousPage();
      } catch (error) {
        console.log('error', error);
        this.notiError();
      } finally {
        this.disabled = false;
      }
    }
  }
};
</script>

<style lang="scss">
.text_editor__required {
  .ql-container.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 0 0 4.64px 4.64px !important;
  }

  .ql-toolbar.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 4.64px 4.64px 0 0 !important;
    border-bottom: none !important;
  }
}

.text_feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}

// .custom-control-label {
//   position: relative;
//   margin-bottom: 0;
//   vertical-align: top;
//   width: 100%;
// }

.long-tooltip.tooltip.b-tooltip {
  opacity: 1 !important;
}
.long-tooltip .tooltip-inner {
  background-color: #fff !important;
  max-width: 1000px !important;
  line-height: 22px;
  padding: 16px;
}

@media screen and (max-width: 992px) {
  .long-tooltip .tooltip-inner {
    max-width: 700px !important;
  }
}

@media screen and (max-width: 768px) {
  .long-tooltip .tooltip-inner {
    max-width: 500px !important;
  }
}

@media screen and (max-width: 576px) {
  .long-tooltip .tooltip-inner {
    max-width: 300px !important;
  }
}
@media screen and (max-width: 375px) {
  .long-tooltip .tooltip-inner {
    max-width: 200px !important;
  }
}
</style>
